import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { Search, MessageSquare, ChevronLeft, ChevronRight, Filter, X, Paperclip, Loader2 } from "lucide-react";

interface MessageData {
  id: string;
  fromUserId: string;
  toUserId: string;
  loadId: string | null;
  message: string;
  messageType: string;
  attachmentUrl: string | null;
  attachmentName: string | null;
  readAt: string | null;
  createdAt: string;
}

interface UserInfo {
  id: string;
  username: string;
  companyName: string;
  role: string;
}

interface AdminMessagesResponse {
  messages: MessageData[];
  users: Record<string, UserInfo>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export default function AdminMessages() {
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", "50");
    if (search) params.set("search", search);
    if (selectedUserId) params.set("userId", selectedUserId);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    return params.toString();
  };

  const { data, isLoading } = useQuery<AdminMessagesResponse>({
    queryKey: [`/api/admin/messages?${buildQueryString()}`],
  });

  const { data: allUsersData } = useQuery<UserInfo[]>({
    queryKey: ["/api/admin/users"],
  });

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setSearchInput("");
    setSelectedUserId("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasFilters = search || selectedUserId || dateFrom || dateTo;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  const getUserDisplay = (userId: string) => {
    const user = data?.users[userId];
    if (!user) return userId.substring(0, 8) + "...";
    return user.companyName || user.username || userId.substring(0, 8);
  };

  const getRoleBadgeVariant = (role: string): "default" | "secondary" | "outline" => {
    switch (role) {
      case "shipper": return "default";
      case "carrier": return "secondary";
      case "broker": return "outline";
      default: return "outline";
    }
  };

  const truncateMessage = (msg: string, maxLen = 120) => {
    if (msg.length <= maxLen) return msg;
    return msg.substring(0, maxLen) + "...";
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-admin-messages-title">Messages</h1>
          <p className="text-muted-foreground text-sm">
            Browse and search all member conversations
            {data?.pagination && (
              <span> - {data.pagination.total.toLocaleString()} total messages</span>
            )}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex gap-2 flex-1 min-w-[200px]">
              <Input
                placeholder="Search message content..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                data-testid="input-search-messages"
              />
              <Button onClick={handleSearch} data-testid="button-search-messages">
                <Search className="h-4 w-4" />
              </Button>
            </div>

            <Select value={selectedUserId || "all"} onValueChange={(v) => { setSelectedUserId(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-[220px]" data-testid="select-user-filter">
                <SelectValue placeholder="Filter by user" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {allUsersData?.map((u: any) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.companyName || u.username || u.id.substring(0, 8)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="w-[160px]"
              placeholder="From date"
              data-testid="input-date-from"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="w-[160px]"
              placeholder="To date"
              data-testid="input-date-to"
            />

            {hasFilters && (
              <Button variant="outline" onClick={handleClearFilters} data-testid="button-clear-filters">
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !data?.messages?.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <MessageSquare className="h-10 w-10 mb-3 opacity-50" />
              <p className="font-medium">No messages found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">From</TableHead>
                  <TableHead className="w-[180px]">To</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead className="w-[90px]">Load</TableHead>
                  <TableHead className="w-[80px] text-center">Status</TableHead>
                  <TableHead className="w-[160px]">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.messages.map((msg) => {
                  const fromUser = data.users[msg.fromUserId];
                  const toUser = data.users[msg.toUserId];
                  return (
                    <TableRow key={msg.id} data-testid={`row-message-${msg.id}`}>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium truncate max-w-[160px]" title={getUserDisplay(msg.fromUserId)}>
                            {getUserDisplay(msg.fromUserId)}
                          </span>
                          {fromUser?.role && (
                            <Badge variant={getRoleBadgeVariant(fromUser.role)} className="text-[10px] w-fit capitalize">
                              {fromUser.role}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium truncate max-w-[160px]" title={getUserDisplay(msg.toUserId)}>
                            {getUserDisplay(msg.toUserId)}
                          </span>
                          {toUser?.role && (
                            <Badge variant={getRoleBadgeVariant(toUser.role)} className="text-[10px] w-fit capitalize">
                              {toUser.role}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          <p className="text-sm text-muted-foreground" data-testid={`text-message-content-${msg.id}`}>
                            {truncateMessage(msg.message)}
                          </p>
                          {msg.attachmentName && (
                            <Badge variant="outline" className="text-[10px] shrink-0">
                              <Paperclip className="h-3 w-3 mr-0.5" />
                              File
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {msg.loadId ? (
                          <Badge variant="outline" className="text-[10px] font-mono">
                            {msg.loadId.substring(0, 8)}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {msg.readAt ? (
                          <Badge variant="secondary" className="text-[10px]">Read</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">Unread</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(msg.createdAt)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground" data-testid="text-pagination-info">
            Page {data.pagination.page} of {data.pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              data-testid="button-prev-page"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= data.pagination.totalPages}
              onClick={() => setPage(p => p + 1)}
              data-testid="button-next-page"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
